import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { SvgIconComponent } from '@ui-lib/components';
import { BiometricAuthService } from '../../core/services/auth/biometric-auth.service';

@Component({
  selector: 'rrp-pin-pad',
  templateUrl: './pin-pad.component.html',
  styleUrl: './pin-pad.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NgOptimizedImage, NgClass, SvgIconComponent],
})
export class PinPadComponent implements OnInit {
  private readonly biometricService = inject(BiometricAuthService);

  public readonly titleTranslateKey = input<string>('pin.enter-code');
  public readonly errorTranslateKey = input<string | undefined>();
  public readonly showBiometric = input<boolean>(true);
  public readonly showLogout = input<boolean>(true);
  public readonly logoutTextKey = input<string>('pin.logout');
  public readonly pinLength = input<number>(4);

  protected readonly linkedErrorTranslateKey = linkedSignal(
    this.errorTranslateKey,
  );

  public readonly biometricClick = output<void>();
  public readonly pinEntered = output<string>();
  public readonly logoutClick = output<void>();

  protected readonly pinCode = signal<string[]>([]);
  protected readonly biometricAvailable = signal(false);

  protected readonly keyboard = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
  ];

  public ngOnInit(): void {
    this.checkBiometric();
  }

  private async checkBiometric(): Promise<void> {
    try {
      const result = await this.biometricService.check();
      this.biometricAvailable.set(result.strongBiometryIsAvailable);
    } catch {
      this.biometricAvailable.set(false);
    }
  }

  public onNumberClick(num: number): void {
    const currentLength = this.pinCode().length;
    const maxLength = this.pinLength();

    if (currentLength < maxLength) {
      this.pinCode.update((pin) => [...pin, num.toString()]);

      this.linkedErrorTranslateKey.set(undefined);

      if (this.pinCode().length === maxLength) {
        this.emitPin();
      }
    }
  }

  public onDelete(): void {
    if (this.pinCode().length > 0) {
      this.pinCode.update((pin) => pin.slice(0, -1));
      this.linkedErrorTranslateKey.set(undefined);
    }
  }

  public onBiometric(): void {
    this.biometricClick.emit();
  }

  public onLogout(): void {
    this.logoutClick.emit();
  }

  public reset(): void {
    this.pinCode.set([]);
  }

  public setError(errorKey: string): void {
    this.linkedErrorTranslateKey.set(errorKey);
    this.reset();
  }

  private emitPin(): void {
    const pin = this.pinCode().join('');
    this.pinEntered.emit(pin);
  }

  protected getIndicators(): number[] {
    return Array.from({ length: this.pinLength() }, (_, i) => i);
  }
}
